<template>
  <div
    :class="getLoading && 'bg-white'"
    class="flex-auto px-4 lg:px-6 py-6 pt-0"
  >
    <div
      v-if="getLoading"
      class="bg-white w-full flex flex-col py-10 justify-center items-center"
    >
      <!-- https://gannon.tv/intro -->
      <img width="260" src="@/assets/imgs/loading.gif" alt="loading" />
      <h3>Loading...</h3>
    </div>
    <form v-if="!getLoading">
      <div class="flex justify-between items-center pr-4">
        <h6
          class="pl-4 text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase"
        >
          Basic
        </h6>
        <button
          @click="handleAddHotspot"
          type="button"
          class="bg-gray-500 text-white active:bg-gray-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
        >
          Add Hotspot
        </button>
      </div>
      <div class="flex flex-wrap">
        <div class="w-full lg:w-6/12 px-4">
          <div class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                title="Instance Name"
                description="The name of instance"
              />
            </label>
          </div>
          <input
            type="text"
            v-model="formDataLocal.instance_name"
            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
          />
        </div>
        <div
          v-for="(value, name) in slideAttributes"
          :key="name"
          class="w-full lg:w-6/12 px-4"
        >
          <div v-if="value.type === 'short_text'" class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="name"
                :description="value.description"
              />
            </label>
            <input
              type="text"
              v-model="formDataLocal['attributes'][name]"
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
            />
          </div>
          <div
            v-else-if="value.type === 'select' || value.type === 'boolean'"
            class="relative w-full mb-3"
          >
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="name"
                :description="value.description"
              />
            </label>
            <select
              v-model="formDataLocal['attributes'][name]"
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
            >
              <option
                v-for="(option, index) in changeOptionsFormat(value.options)"
                :key="index"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div v-else-if="value.type === 'url'" class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="name"
                :description="value.description"
              />
            </label>
            <URLInput
              :url="
                formData['attributes'][name] ? formData['attributes'][name] : ''
              "
              @update:url="(val) => handleUpdateValue(val, name)"
            />
          </div>
          <div v-else-if="value.type === 'icon'" class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="name"
                :description="value.description"
              />
            </label>
            <IconPicker
              :icons="value.options"
              :icon="
                formData['attributes'][name] ? formData['attributes'][name] : ''
              "
              @update:icon="(val) => handleUpdateValue(val, name)"
            />
          </div>
          <div v-else class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="name"
                :description="value.description"
              />
            </label>
            <input
              v-model="formDataLocal['attributes'][name]"
              type="text"
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
            />
          </div>
        </div>
        <div class="w-full lg:w-12/12 px-4">
          <div class="relative w-full mb-3">
            <ImagePreview
              :formData="formData['attributes']"
              :dotSize="44"
              :imageSize="900"
              @update:formData="handleUpdateHotspot"
            />
          </div>
        </div>
      </div>
      <HotspotPanel
        v-for="(hotspot, index) in formDataLocal['attributes']['hotspots']"
        :hotspotId="index"
        :key="index"
        :allSlideAttributes="allSlideAttributes"
        :formData="formData['attributes']"
        @update:formData="handleUpdateHotspot"
      />
    </form>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import HotspotPanel from "@/components/ComponentModule/ImageSlideComponent/HotspotPanel";
import AttrTitleWithDescription from "@/components/Shared/Headers/AttrTitleWithDescription";
import ImagePreview from "@/components/Shared/Input/ImagePreview";
import URLInput from "@/components/Shared/Input/URLInput";
import IconPicker from "@/components/Shared/Input/IconPicker";

export default {
  name: "ImageSlideForm",
  components: {
    HotspotPanel,
    ImagePreview,
    AttrTitleWithDescription,
    URLInput,
    IconPicker
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    allSlideAttributes: {
      type: Object,
      required: false
    }
  },
  computed: {
    slideAttributes() {
      if (this.allSlideAttributes) {
        let { hotspots, ...params } = this.allSlideAttributes;// eslint-disable-line
        return params;
      }
      return {};
    },
    formDataLocal: {
      get: function () {
        return this.formData;
      },
      set: function (val) {
        this.$emit("update:formData", val);
      }
    },
    ...mapGetters(["getAllAttributes", "getLoading"])
  },
  watch: {
    slideAttributes() {
      if (this.slideAttributes && Object.keys(this.slideAttributes).length) {
        Object.keys(this.slideAttributes).forEach((item) => {
          if (!this.formDataLocal["attributes"][item]) {
            this.formDataLocal["attributes"][item] =
              this.slideAttributes[item].value;
          }
        });
      }
    }
  },
  methods: {
    changeOptionsFormat(options) {
      const newOptions = options.map((item) => {
        return { label: item, value: item };
      });
      return newOptions;
    },
    handleUpdateHotspot(val) {
      this.formDataLocal["attributes"] = val;
    },
    handleAddHotspot() {
      this.formDataLocal["attributes"]["hotspots"].push({
        pos_left: 0,
        pos_top: 0
      });
    }
  }
};
</script>
